import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CodeOutlined,
  EllipsisOutlined,
  FolderOpenOutlined,
  GlobalOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  LockOutlined,
  MenuOutlined,
  PictureOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  TagsOutlined,
  ThunderboltOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { ROUTE_TRANSLATION_KEYS, ROUTES } from '../../routes/constants';
import { useTranslation } from '../../hooks/useTranslation';
import { isEnglishSite } from '../../config/site';
import { buildImgCloudRoutePath } from '../../utils/appRouting';
import { useUserInfo } from '../../contexts/UserInfoContext';
import {
  DEFAULT_HEADER_MENU_ITEMS,
  type HeaderMenuItem,
  type InternalHeaderMenuKey,
  loadHeaderMenu,
} from '../../services/headerMenu';
import './Navigation.css';

export const ORGANIZATION_ROUTES = [
  ROUTES.DIRECTORIES,
  ROUTES.INTERNATIONAL,
  ROUTES.IMAGE_CATEGORIES,
] as const;

interface InternalMenuDefinition {
  path: string;
  icon: React.ReactNode;
}

const INTERNAL_MENU_DEFINITIONS: Readonly<Record<InternalHeaderMenuKey, InternalMenuDefinition>> = {
  home: { path: ROUTES.HOME, icon: <HomeOutlined /> },
  'web-hosting': { path: ROUTES.WEB_HOSTING, icon: <CodeOutlined /> },
  galleries: { path: ROUTES.GALLERIES, icon: <PictureOutlined /> },
  'image-processing': { path: ROUTES.IMAGE_PROCESSING, icon: <ToolOutlined /> },
  'image-search': { path: ROUTES.IMAGE_SEARCH, icon: <SearchOutlined /> },
  'ai-generate': { path: ROUTES.AI_GENERATE, icon: <ThunderboltOutlined /> },
  'encrypted-links': { path: ROUTES.ENCRYPTED_LINKS, icon: <LockOutlined /> },
  'sale-links': { path: ROUTES.SALE_LINKS, icon: <LinkOutlined /> },
  directories: { path: ROUTES.DIRECTORIES, icon: <FolderOpenOutlined /> },
  international: { path: ROUTES.INTERNATIONAL, icon: <GlobalOutlined /> },
  'image-categories': { path: ROUTES.IMAGE_CATEGORIES, icon: <TagsOutlined /> },
  faq: { path: ROUTES.FAQ, icon: <QuestionCircleOutlined /> },
  about: { path: ROUTES.ABOUT, icon: <InfoCircleOutlined /> },
};

type AntMenuItem = NonNullable<MenuProps['items']>[number];

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, currentLanguage } = useTranslation();
  const { config } = useUserInfo();
  const [configuredItems, setConfiguredItems] = useState<HeaderMenuItem[]>([
    ...DEFAULT_HEADER_MENU_ITEMS,
  ]);
  const enableImageSearch = config.enableImageSearch === true;
  const englishSite = isEnglishSite();
  const selectedPath = location.pathname === '/index.html' || location.pathname === '/img-cloud/index.html'
    ? ROUTES.HOME
    : location.pathname;

  useEffect(() => {
    let active = true;
    void loadHeaderMenu().then((items) => {
      if (active) setConfiguredItems(items);
    });
    return () => {
      active = false;
    };
  }, []);

  const visibleItems = useMemo(() => configuredItems.filter((item) => {
    if (item.kind === 'external') {
      return !(englishSite && item.id === 'blog');
    }
    if (item.menuKey === 'image-search' && !enableImageSearch) {
      return false;
    }
    return !(englishSite && ['ai-generate', 'faq', 'about'].includes(item.menuKey));
  }), [configuredItems, enableImageSearch, englishSite]);

  const handleInternalLinkClick = (event: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.altKey
      || event.ctrlKey
      || event.shiftKey
    ) {
      return;
    }
    event.preventDefault();
    navigate(path);
  };

  const renderInternalLink = (path: string, label: React.ReactNode) => (
    <a
      className="navigation-link"
      href={buildImgCloudRoutePath(path)}
      onClick={(event) => handleInternalLinkClick(event, path)}
    >
      {label}
    </a>
  );

  const renderExternalLink = (url: string, label: React.ReactNode) => (
    <a className="navigation-link" href={url} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  );

  const toMenuItem = (item: HeaderMenuItem): AntMenuItem | null => {
    if (item.kind === 'external') {
      const label = currentLanguage === 'en' ? item.labelEn : item.labelZh;
      return {
        key: `external:${item.id}`,
        icon: <LinkOutlined />,
        label: renderExternalLink(item.url, label),
      };
    }
    const definition = INTERNAL_MENU_DEFINITIONS[item.menuKey];
    if (!definition) return null;
    const translationKey = ROUTE_TRANSLATION_KEYS[definition.path as keyof typeof ROUTE_TRANSLATION_KEYS];
    return {
      key: definition.path,
      icon: definition.icon,
      label: renderInternalLink(definition.path, t(translationKey)),
    };
  };

  const buildItemsForPlacement = (placement: 'primary' | 'more'): AntMenuItem[] =>
    visibleItems
      .filter((item) => item.placement === placement)
      .map(toMenuItem)
      .filter((item): item is AntMenuItem => item !== null);

  const primaryMenuItems = buildItemsForPlacement('primary');
  const moreMenuItems = buildItemsForPlacement('more');
  const menuItems: NonNullable<MenuProps['items']> = [
    ...primaryMenuItems,
    ...(moreMenuItems.length > 0 ? [{
      key: 'more',
      icon: <EllipsisOutlined />,
      label: t('navigation.more'),
      children: moreMenuItems,
    }] : []),
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key, domEvent }) => {
    const target = domEvent.target as HTMLElement | null;
    if (target?.closest('a')) return;

    if (key.startsWith('external:')) {
      const externalId = key.slice('external:'.length);
      const externalItem = visibleItems.find(
        (item): item is Extract<HeaderMenuItem, { kind: 'external' }> =>
          item.kind === 'external' && item.id === externalId,
      );
      if (externalItem) window.open(externalItem.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (key !== 'more') navigate(key);
  };

  return (
    <>
      <div className="desktop-navigation">
        <Menu
          mode="horizontal"
          selectedKeys={[selectedPath]}
          items={menuItems}
          onClick={handleMenuClick}
          forceSubMenuRender
          style={{
            border: 'none',
            background: 'transparent',
            flex: 1,
            marginLeft: '0px',
          }}
        />
      </div>

      <div className="mobile-navigation">
        <Dropdown
          menu={{
            items: menuItems,
            selectedKeys: [selectedPath],
            onClick: handleMenuClick,
            forceSubMenuRender: true,
          }}
          placement="bottomRight"
          trigger={['click']}
        >
          <Button
            type="text"
            icon={<MenuOutlined />}
            style={{
              fontSize: '18px',
              color: '#4C5362',
              border: 'none',
              padding: '8px',
              height: '40px',
              width: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        </Dropdown>
      </div>
    </>
  );
};

export default Navigation;
